import { ButtonIcon, FieldGroup, FieldSection, Header } from '@/ui'
import { Controller, useForm } from 'react-hook-form'
import { PickOneItem } from '@/api/dashboard'
import { useTranslation } from 'react-i18next'
import { Button, FormErrorMessage, Input, formatHookFormErrors } from '@dashboard/ui'
import { CloseOutline } from '@/icons'
import { SelectSkuItemV2 } from '@/layouts/components/SelectSkuItemV2'
import { ErrorMessage } from '@hookform/error-message'
import { useGenerateCompanyPath } from '@/libs'
import { useGameItem } from '@/api/useGameItems'

interface ItemFormProps {
  onClose: () => void
  onSubmit: (item: PickOneItem) => void
  item: PickOneItem
  title: string
}

export const ItemForm = (props: ItemFormProps) => {
  const { t } = useTranslation()
  const { companyId, gameId } = useGenerateCompanyPath()
  const {
    register,
    formState: { errors, isSubmitting, isDirty },
    handleSubmit,
    control,
    watch,
    setValue,
  } = useForm<PickOneItem>({
    values: {
      ...props.item,
    },
  })

  const { data: item } = useGameItem(companyId, gameId, watch('item_id'))

  const onSubmit = (data: PickOneItem) => {
    props.onClose()
    props.onSubmit(data)
  }

  const renderGeneral = () => {
    return (
      <FieldSection label={t('general')}>
        <FieldGroup label={t('item.item')}>
          <Controller
            name="item_id"
            rules={{ required: t('validation.required') }}
            control={control}
            render={({ field: { onChange, value } }) => (
              <SelectSkuItemV2
                hidePrice={true}
                onChange={(v, selectedSkuItem) => {
                  onChange(v as string)
                  if (selectedSkuItem?.is_stackable) {
                    setValue('quantity', selectedSkuItem.quantity, { shouldDirty: true })
                  }
                }}
                value={value}
              />
            )}
          />

          <ErrorMessage
            name="item_id"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
        {item?.is_stackable && (
          <FieldGroup label={t('sku.item.quantity')}>
            <Input
              min={1}
              type={'number'}
              {...register('quantity', {
                required: t('validation.required'),
              })}
              {...formatHookFormErrors(errors, 'quantity')}
            />
          </FieldGroup>
        )}
      </FieldSection>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex h-full flex-col bg-fg-primary">
        <Header title={props.title} className="sticky top-0 z-50 p-4" minWidth="auto">
          <div className="flex items-center gap-4">
            <Button disabled={!isDirty} loading={isSubmitting} type="submit">
              {t('Apply')}
            </Button>
            <ButtonIcon onClick={props.onClose} type="button" size="xs" variant="outline" color="secondary">
              <CloseOutline />
            </ButtonIcon>
          </div>
        </Header>

        <div className="flex size-full flex-col p-4">{renderGeneral()}</div>
      </div>
    </form>
  )
}
